import links from '@/config/links'
import getAppRootPath, { withoutAppRootPath } from '@/utils/getAppRootPath'

const STORAGE_KEY = 'emma.checkoutLeave'
const storageKey = () => `${STORAGE_KEY}:${getAppRootPath() || '/'}`

// Signup is a full page load for every checkout, so these routes stay inside the purchase.
const SHARED_KEEP_ROUTES = new Set([
	links.register.path,
	links.signup_complete.path,
	links.pre_auth_card.path,
	links.login.path,
])

const definitions = new Map()

let active = false
let activeId = null
let allowedUnload = null
let leaveVariant = null
let dialogOpen = false
let dialogId = 0
let openDialog = null
let destroyDialog = null
let navigateImpl = null
let restoringPop = null
let stayHref = ''
let stayState = null
let terminology = null
const promptListeners = new Set()

const normalizePath = (pathname) => {
	if (!pathname) return '/'
	if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1)
	return pathname
}

const parseStored = () => {
	try {
		const raw = sessionStorage.getItem(storageKey())
		if (!raw) return null
		return JSON.parse(raw)
	} catch (error) {
		return null
	}
}

const isTerminology = (value) =>
	!!value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0

const readSession = () => {
	const parsed = parseStored()
	if (!parsed?.id || !definitions.has(parsed.id)) return null
	if (!definitions.get(parsed.id).variants[parsed.variant]) return null
	if (isTerminology(parsed.terminology)) terminology = parsed.terminology
	return parsed
}

const writeSession = (id, variant) => {
	try {
		const stored = parseStored()
		const storedTerminology = isTerminology(stored?.terminology) ? stored.terminology : null
		const nextTerminology = terminology || storedTerminology
		if (!terminology && nextTerminology) terminology = nextTerminology
		const payload = { id, variant }
		if (nextTerminology) payload.terminology = nextTerminology
		sessionStorage.setItem(storageKey(), JSON.stringify(payload))
	} catch (error) {
		// Private mode can block storage. The in-memory flag still covers this page.
	}
}

const clearSession = () => {
	try {
		sessionStorage.removeItem(storageKey())
	} catch (error) {
		// Ignore storage failures.
	}
}

const definitionKeepsPath = (definition, path) =>
	!!definition && (definition.keepRoutes.has(path) || SHARED_KEEP_ROUTES.has(path))

const amountFromTotal = (total) => {
	if (total === null || total === undefined || total === '') return null
	const amount = typeof total === 'number' ? total : Number(String(total).replace(/,/g, ''))
	return Number.isFinite(amount) ? amount : null
}

const variantFromTotal = (definition, total) => {
	if (typeof definition.variantFromTotal === 'function') {
		return definition.variantFromTotal(total)
	}

	const variantIds = Object.keys(definition.variants || {})
	if (variantIds.length === 1) return variantIds[0]

	const amount = amountFromTotal(total)
	if (amount === null) return null
	if (definition.variants.free && amount <= 0) return 'free'
	if (definition.variants.paid && amount > 0) return 'paid'
	return null
}

export const registerCheckoutLeaveDefinition = (definition) => {
	if (!definition?.id || !definition.variants) return
	definitions.set(definition.id, {
		...definition,
		keepRoutes: new Set(definition.keepRoutes || []),
	})
}

export const checkoutLeaveIdForOrigin = (origin) => (definitions.has(origin) ? origin : null)

export const getCheckoutLeaveCopy = () => {
	const definition = definitions.get(activeId)
	if (!definition) return null
	if (typeof definition.copyFor === 'function') {
		return (
			definition.copyFor(leaveVariant, terminology) || definition.variants?.[leaveVariant] || null
		)
	}
	return definition.variants?.[leaveVariant] || null
}

export const setCheckoutLeaveTerminology = (next) => {
	if (!isTerminology(next)) return
	terminology = next
	if (active && activeId && leaveVariant) writeSession(activeId, leaveVariant)
}

export const isCheckoutLeaveActive = () => active

export const shouldPromptCheckoutLeave = () => active

export const shouldPromptCheckoutUnload = () => {
	const allowance = allowedUnload
	allowedUnload = null
	if (!active) return false
	if (allowance && !allowance.event?.defaultPrevented && !allowance.event?.signal?.aborted) {
		return false
	}
	return true
}

const isWithinApp = (url) => {
	const root = getAppRootPath()
	return (
		url.origin === window.location.origin &&
		(!root || url.pathname === root || url.pathname.startsWith(`${root}/`))
	)
}

// Only known checkout/auth destinations bypass this one document unload.
// The pending basket stays in session storage for the destination page.
export const redirectWithinCheckout = (href, { replace = false } = {}) => {
	const url = new URL(href, window.location.href)
	const path = normalizePath(withoutAppRootPath(url.pathname))
	if (
		active &&
		isWithinApp(url) &&
		definitionKeepsPath(definitions.get(activeId), path) &&
		(url.pathname !== window.location.pathname || url.search !== window.location.search)
	) {
		allowedUnload = {}
	}
	try {
		if (replace) window.location.replace(href)
		else window.location.assign(href)
	} catch (error) {
		allowedUnload = null
		throw error
	}
}

// Observe the actual SDK redirect, without disabling tab-close/reload protection
// while Stripe validates or waits for a response. Older browsers keep the prompt.
export const withCheckoutPaymentRedirect = async (confirm) => {
	const navigation = window.navigation
	if (!active || !navigation?.addEventListener) return confirm()
	let allowance = null
	const onNavigate = (event) => {
		if (
			event.defaultPrevented ||
			event.userInitiated !== false ||
			event.destination.sameDocument ||
			!['push', 'replace'].includes(event.navigationType) ||
			event.downloadRequest != null
		)
			return
		const url = new URL(event.destination.url)
		if (url.protocol !== 'https:' || url.origin === window.location.origin) return
		allowance = { event }
		allowedUnload = allowance
	}
	navigation.addEventListener('navigate', onNavigate)
	try {
		return await confirm()
	} finally {
		navigation.removeEventListener('navigate', onNavigate)
		if (allowedUnload === allowance) allowedUnload = null
	}
}

export const hasPendingCheckoutLeave = (id) => {
	if (id) {
		if (active && activeId === id) return true
		return readSession()?.id === id
	}
	return active || !!readSession()
}

const rememberStay = () => {
	if (typeof window === 'undefined' || restoringPop) return
	stayHref = window.location.href
	stayState = window.history.state
}

export const armCheckoutLeave = (id, variant) => {
	const definition = definitions.get(id)
	if (!definition?.variants?.[variant]) return
	active = true
	activeId = id
	leaveVariant = variant
	writeSession(id, variant)
	rememberStay()
}

export const armCheckoutLeaveFromTotal = (id, total) => {
	const definition = definitions.get(id)
	if (!definition) return
	const variant = variantFromTotal(definition, total)
	if (!variant) return
	armCheckoutLeave(id, variant)
}

export const resumeCheckoutLeave = () => {
	allowedUnload = null
}

const closeDialog = () => {
	dialogOpen = false
	dialogId += 1
	const destroy = destroyDialog
	destroyDialog = null
	destroy?.()
}

export const releaseCheckoutLeave = () => {
	active = false
	activeId = null
	allowedUnload = null
	restoringPop = null
	leaveVariant = null
	terminology = null
	clearSession()
	closeDialog()
}

export const registerCheckoutLeaveNavigate = (navigate) => {
	navigateImpl = navigate
}

export const registerCheckoutLeaveOpener = (opener) => {
	openDialog = opener
}

export const registerCheckoutLeaveDestroy = (destroy) => {
	destroyDialog = destroy
}

export const markCheckoutLeaveDialogClosed = (id) => {
	if (id !== dialogId) return
	dialogOpen = false
	destroyDialog = null
}

export const syncCheckoutLeaveRoute = (pathname) => {
	const path = normalizePath(pathname)
	const saved = readSession()
	const savedKeeps = definitionKeepsPath(definitions.get(saved?.id), path)
	if (savedKeeps) {
		// A page may already have armed a fresher total. Don't overwrite it.
		if (!active) armCheckoutLeave(saved.id, saved.variant)
		else rememberStay()
		return
	}

	const activeKeeps = active && definitionKeepsPath(definitions.get(activeId), path)
	if (activeKeeps) {
		rememberStay()
		return
	}

	if (active || saved) releaseCheckoutLeave()
}

const isPlainLeftClick = (event) => {
	if (!event || event.defaultPrevented) return false
	if (typeof event.button === 'number' && event.button !== 0) return false
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false
	return true
}

const isCurrentLocation = (href) => {
	try {
		const url = new URL(href, window.location.href)
		return (
			url.origin === window.location.origin &&
			url.pathname === window.location.pathname &&
			url.search === window.location.search
		)
	} catch (error) {
		return false
	}
}

export const followLeaveDestination = (href) => {
	let url
	try {
		url = new URL(href, window.location.href)
	} catch (error) {
		releaseCheckoutLeave()
		window.location.assign(href)
		return
	}

	// Mail and phone links stay on this page, so the purchase warning stays armed.
	if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
		closeDialog()
		window.location.assign(url.href)
		return
	}

	releaseCheckoutLeave()

	if (!isWithinApp(url)) {
		window.location.assign(url.href)
		return
	}

	const path = withoutAppRootPath(`${url.pathname}${url.search}${url.hash}`)
	if (navigateImpl) {
		navigateImpl(path)
		return
	}
	window.location.assign(url.href)
}

export const registerCheckoutLeavePromptListener = (listener) => {
	promptListeners.add(listener)
	return () => promptListeners.delete(listener)
}

const presentLeaveDialog = (request) => {
	if (!active || dialogOpen || !openDialog || !getCheckoutLeaveCopy()) return
	dialogId += 1
	dialogOpen = true
	promptListeners.forEach((listener) => listener())
	openDialog({ ...request, dialogId })
}

const anchorFromEvent = (event) => {
	const target = event?.target
	if (!target || typeof target.closest !== 'function') return null
	return target.closest('a[href]')
}

const shouldIgnoreAnchor = (anchor) => {
	if (!anchor || anchor.dataset?.checkoutLeaveIgnore === 'true') return true
	if (anchor.getAttribute('target') === '_blank') return true
	if (anchor.hasAttribute('download')) return true
	const href = anchor.href
	if (!href || isCurrentLocation(href)) return true
	try {
		const protocol = new URL(href, window.location.href).protocol
		if (protocol !== 'http:' && protocol !== 'https:') return true
	} catch (error) {
		return true
	}
	return false
}

const onDocumentClick = (event) => {
	if (!active || !getCheckoutLeaveCopy()) return
	if (!isPlainLeftClick(event)) return
	const anchor = anchorFromEvent(event)
	if (shouldIgnoreAnchor(anchor)) return
	event.preventDefault()
	event.stopPropagation()
	presentLeaveDialog({ href: anchor.href })
}

const onPopState = (event) => {
	if (!shouldPromptCheckoutLeave()) return
	if (restoringPop) {
		// Don't let the router unmount the current checkout during the restoration.
		event.stopImmediatePropagation()
		if (window.location.href !== stayHref) return
		const { delta } = restoringPop
		restoringPop = null
		presentLeaveDialog({
			onLeave: () => {
				releaseCheckoutLeave()
				window.history.go(-delta)
			},
		})
		return
	}
	if (!stayHref || window.location.href === stayHref) return

	const popped = window.history.state || {}
	if (typeof stayState?.idx === 'number' && typeof popped.idx === 'number') {
		const delta = stayState.idx - popped.idx
		if (delta !== 0) {
			event.stopImmediatePropagation()
			restoringPop = { delta }
			window.history.go(delta)
			return
		}
	}

	// Entries created outside the router may not have an index to restore.
	const idx = (typeof popped.idx === 'number' ? popped.idx : 0) + 1
	const nextState = stayState || { ...popped, idx }
	try {
		window.history.pushState(nextState, '', stayHref)
	} catch (error) {
		return
	}

	presentLeaveDialog({
		onLeave: () => {
			releaseCheckoutLeave()
			window.history.back()
		},
	})
}

if (typeof window !== 'undefined' && !window.__emmaCheckoutLeaveGuard) {
	window.__emmaCheckoutLeaveGuard = true
	window.addEventListener('popstate', onPopState)
	document.addEventListener('click', onDocumentClick, true)
	if (import.meta.hot) {
		import.meta.hot.dispose(() => {
			window.removeEventListener('popstate', onPopState)
			document.removeEventListener('click', onDocumentClick, true)
			window.__emmaCheckoutLeaveGuard = false
		})
	}
}

export const requestCheckoutLeave = (event) => {
	if (!active || !getCheckoutLeaveCopy()) return false
	if (!isPlainLeftClick(event)) return false

	const anchor = event.currentTarget
	if (!anchor || anchor.getAttribute?.('target') === '_blank') return false

	const href = anchor.href
	if (!href || isCurrentLocation(href)) return false

	event.preventDefault()
	presentLeaveDialog({ href })
	return true
}

export const confirmCheckoutLeave = (onLeave) => {
	if (!active || !openDialog || !getCheckoutLeaveCopy()) {
		onLeave?.()
		return
	}
	if (dialogOpen) return
	presentLeaveDialog({ onLeave })
}
